/**
 * @file src/_modulos/prenota/tabela/layouts/config/api/query.ts
 * @description Query simplificada para pré-notas
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas gerencia queries
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { URL } from "api";
import {
  type UsePrenotasOptions,
  type FetchPrenotasResponse,
  type ListaPrenotasParams,
  tableCache,
} from "@PN/tabela";

/* -------------------------------------------------------------------------- */
/* CONFIGURAÇÕES                                                              */
/* -------------------------------------------------------------------------- */

const ENDPOINT = URL.in.API_PRENOTAS_LIST;
export const PRENOTAS_QUERY_KEY = ["prenotas"] as const;

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Adapta a resposta da API para o formato esperado
 */
function adaptApiResponse(responseData: any): FetchPrenotasResponse {
  return {
    success: responseData.success ?? true,
    data: responseData.data || [],
    total: responseData.total || 0,
    page: responseData.page || 1,
    pageSize: responseData.pageSize || 10,
    totalPages: responseData.totalPages || 1,
    hasNext: responseData.hasNext || false,
    hasPrev: responseData.hasPrev || false,
    pagination: {
      page: responseData.page || 1,
      pageSize: responseData.pageSize || 10,
      totalCount: responseData.total || 0,
      totalPages: responseData.totalPages || 1,
    },
  };
}

/* -------------------------------------------------------------------------- */
/* HOOK PRINCIPAL                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Hook simplificado para buscar pré-notas
 */
export function usePrenotas(options: UsePrenotasOptions) {
  const { page = 1, pageSize = 10, searchTerm = "", filiais = [] } = options;

  // Verifica se query está habilitada
  const isEnabled = filiais.length > 0;

  // Toast informativo quando query não está habilitada (apenas uma vez)
  useEffect(() => {
    if (!isEnabled) {
      const timeoutId = setTimeout(() => {
        toast.error("⏳ Aguardando carregamento das filiais...");
      }, 1000); // Delay para evitar toast imediato

      return () => clearTimeout(timeoutId);
    }
  }, [isEnabled]);

  // Query principal
  const query = useQuery<FetchPrenotasResponse>({
    queryKey: [...PRENOTAS_QUERY_KEY, options] as const,
    queryFn: async ({ signal }) => {
      // Usa todos os parâmetros do options (incluindo filtros convertidos)
      const params: ListaPrenotasParams = {
        // Inclui todos os parâmetros e filtros convertidos que vêm do buildApiPayload
        ...options,
      };

      // Debug: log dos parâmetros sendo enviados
      console.log("🔍 [usePrenotas] Parâmetros enviados:", params);

      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]),
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      const responseData = await response.json();
      const processedData = adaptApiResponse(responseData);

      // Salva no cache apenas se a requisição foi bem-sucedida
      if (processedData.success) {
        const cacheKey = `prenotas_${JSON.stringify(params)}`;
        await tableCache.setCache(cacheKey, processedData.data, {
          totalCount: processedData.total,
          hasMore: processedData.hasNext,
          page: processedData.page,
          pageSize: processedData.pageSize,
          filters: {},
        });
      }

      return processedData;
    },
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    enabled: isEnabled,
  });

  return query;
}
